import React, { useState } from "react";
import { format } from "date-fns";
import { MapPin, Mail, Phone, MessageCircle, Clock, CalendarIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import PageLayout from "@/components/layout/PageLayout";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { useSiteContent } from "@/context/SiteContentContext";
import { api, formatErrorDetail } from "@/lib/api";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TOUR_OPTIONS = [
  "Golden Triangle Classic",
  "Golden Triangle Express (3 days)",
  "Golden Triangle + Ranthambore",
  "Spiritual India (Varanasi & Rishikesh)",
  "Char Dham Yatra",
  "Diwali Festival Tour",
  "Holi Festival Tour",
  "Wildlife Tiger Safari",
  "Family India Discovery",
  "Rajasthan Heritage Grand Tour",
  "South India & Kerala",
  "Kashmir Paradise",
  "Leh Ladakh Adventure",
  "Honeymoon Romance Tour",
  "Same Day Taj Mahal Tour",
  "Nepal Extension",
  "Dubai Extension",
  "Thailand Extension",
  "Custom Itinerary",
];

export default function Contact() {
  const c = useSiteContent("contact");
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    tour_interest: "",
    travelers: "2",
    message: "",
  });
  const [date, setDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        travelers: parseInt(form.travelers, 10) || 2,
        travel_date: date ? format(date, "yyyy-MM-dd") : null,
      };
      const { data } = await api.post("/inquiries", payload);
      toast.success(data.message || "Inquiry received!");
      setForm({ name: "", email: "", phone: "", country: "", tour_interest: "", travelers: "2", message: "" });
      setDate(null);
    } catch (err) {
      toast.error(formatErrorDetail(err.response?.data?.detail) || "Failed to send.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageLayout>
      <Seo
        title="Contact Us — Plan Your India Journey"
        description="WhatsApp, email or visit our Agra office. We reply within 24 hours and design fully customised itineraries for India, Nepal, Dubai and Thailand."
      />
      <PageHero
        eyebrow={c.hero_eyebrow}
        title={c.hero_title}
        subtitle={c.hero_subtitle}
        image={c.hero_image}
      />

      <section className="py-24 md:py-32" data-testid="contact-section">
        <div className="container-tti grid lg:grid-cols-12 gap-12">
          {/* INFO */}
          <div className="lg:col-span-5 space-y-10">
            <div>
              <div className="eyebrow mb-3">{c.intro_eyebrow}</div>
              <h2 className="font-serif text-4xl tracking-tight leading-tight mb-6">
                {c.intro_title} <span className="italic text-deepred">{c.intro_title_italic}</span>
              </h2>
              <p className="text-foreground/75 font-light leading-relaxed">
                {c.intro_body}
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="w-11 h-11 rounded-sm bg-saffron-50 text-saffron flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium mb-1">{c.office_label || "Office"}</div>
                  <div className="text-sm text-foreground/70 font-light leading-relaxed">
                    {c.office_address_line_1}<br />
                    {c.office_address_line_2}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-phone">
                <div className="w-11 h-11 rounded-sm bg-saffron-50 text-saffron flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium mb-1">{c.phone_label || "Mobile / WhatsApp"}</div>
                  <a href="tel:+917017798524" className="text-sm text-foreground/70 hover:text-saffron font-light">
                    +91 70177 98524
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-email">
                <div className="w-11 h-11 rounded-sm bg-saffron-50 text-saffron flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium mb-1">{c.email_label || "Email"}</div>
                  <a href={`mailto:${c.email_value || "info@ttiholidays.com"}`} className="text-sm text-foreground/70 hover:text-saffron font-light">
                    {c.email_value || "info@ttiholidays.com"}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-11 h-11 rounded-sm bg-saffron-50 text-saffron flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium mb-1">{c.hours_label || "Hours"}</div>
                  <div className="text-sm text-foreground/70 font-light">
                    {c.hours_value || "Monday – Saturday · 9:00 AM – 8:00 PM IST"}<br />
                    WhatsApp & emergency line: 24/7
                  </div>
                </div>
              </div>
            </div>

            <a
              href="https://wa.me/917017798524"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#1faa54] text-white px-6 py-4 rounded-sm transition-all"
              data-testid="contact-whatsapp-btn"
            >
              <MessageCircle className="w-5 h-5" />
              <div className="text-left">
                <div className="text-[10px] uppercase tracking-[0.25em] opacity-80">Fastest reply</div>
                <div className="font-medium">Chat on WhatsApp</div>
              </div>
            </a>

            <div className="aspect-[4/3] rounded-sm overflow-hidden border border-cream-200">
              <iframe
                title="TTI Holidays Office"
                src="https://www.google.com/maps?q=Tajganj,+Agra+282001&output=embed"
                className="w-full h-full"
                loading="lazy"
              />
            </div>
          </div>

          {/* FORM */}
          <div className="lg:col-span-7">
            <div className="bg-white border border-cream-200 rounded-sm p-8 md:p-12">
              <div className="eyebrow mb-3">Inquiry form</div>
              <h2 className="font-serif text-3xl md:text-4xl tracking-tight mb-2">
                Tell us about your dream trip.
              </h2>
              <p className="text-foreground/70 mb-8 font-light">
                Share a few details below — we&rsquo;ll send back a sample itinerary within 48 hours.
              </p>

              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="cname">Full Name *</Label>
                    <Input
                      id="cname"
                      required
                      data-testid="contact-name"
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Jane Doe"
                      className="bg-cream"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cemail">Email *</Label>
                    <Input
                      id="cemail"
                      type="email"
                      required
                      data-testid="contact-email-field"
                      value={form.email}
                      onChange={update("email")}
                      placeholder="jane@example.com"
                      className="bg-cream"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="cphone">Phone (with country code) *</Label>
                    <Input
                      id="cphone"
                      required
                      data-testid="contact-phone-field"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+44 7700 900123"
                      className="bg-cream"
                    />
                  </div>
                  <div>
                    <Label htmlFor="ccountry">Country</Label>
                    <Input
                      id="ccountry"
                      data-testid="contact-country"
                      value={form.country}
                      onChange={update("country")}
                      placeholder="United Kingdom"
                      className="bg-cream"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label>Tour Interest</Label>
                    <Select value={form.tour_interest} onValueChange={update("tour_interest")}>
                      <SelectTrigger className="bg-cream" data-testid="contact-tour">
                        <SelectValue placeholder="Choose a tour" />
                      </SelectTrigger>
                      <SelectContent>
                        {TOUR_OPTIONS.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Travel Date</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          data-testid="contact-date-trigger"
                          className="w-full h-10 px-3 py-2 bg-cream border border-input rounded-md text-sm text-left flex items-center justify-between"
                        >
                          {date ? format(date, "MMM d, yyyy") : <span className="text-muted-foreground">Pick a date</span>}
                          <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 bg-white" align="start">
                        <Calendar
                          mode="single"
                          selected={date}
                          onSelect={setDate}
                          disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>

                <div>
                  <Label>Travelers</Label>
                  <Select value={form.travelers} onValueChange={update("travelers")}>
                    <SelectTrigger className="bg-cream" data-testid="contact-travelers">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 8, 10, 12].map((n) => (
                        <SelectItem key={n} value={String(n)}>
                          {n} {n === 1 ? "person" : "people"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="cmsg">Message</Label>
                  <Textarea
                    id="cmsg"
                    data-testid="contact-message"
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Tell us about your interests, pace, budget, special requests..."
                    rows={5}
                    className="bg-cream"
                  />
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={submitting}
                  className="btn-primary w-full disabled:opacity-60"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</>
                  ) : (
                    "Send Inquiry"
                  )}
                </button>
                <p className="text-xs text-center text-muted-foreground">
                  We will respond within 24 hours. Your details are never shared.
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </PageLayout>
  );
}
